import clsx from 'clsx';
import {
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  useImperativeHandle,
  useRef,
} from 'react';

import { HoneycombChartLayout } from './components/HoneycombChartLayout.js';
import {
  DEFAULT_HONEYCOMB_HEIGHT,
  DISPLAY_NAME,
  HONEYCOMB_CHART_ARIA_LABEL,
} from './constants.js';
import { HoneycombColorSchemeContext } from './context/honeycomb-color-scheme.context.js';
import { HoneycombFormatterContext } from './context/honeycomb-formatter.context.js';
import { HoneycombLabelsDisplayContext } from './context/honeycomb-labels-display.context.js';
import { HoneycombLoadingContext } from './context/honeycomb-loading.context.js';
import { HoneycombShapeContext } from './context/honeycomb-shape.context.js';
import { HoneycombTextSizeContext } from './context/honeycomb-text-size.context.js';
import { HoneycombTruncationMode } from './context/honeycomb-truncation-mode.context.js';
import { useHoneycombChartConfig } from './hooks/use-honeycomb-chart-config.js';
import { HoneycombBaseScaleProvider } from './providers/honeycomb-base-scale.provider.js';
import { HoneycombConfigurationProvider } from './providers/honeycomb-configuration.provider.js';
import { HoneycombDatasetTypeProvider } from './providers/honeycomb-dataset-type.provider.js';
import { HoneycombFilteredDataProvider } from './providers/honeycomb-filtered-data.provider.js';
import { HoneycombMetadataProvider } from './providers/honeycomb-metadata.provider.js';
import { HoneycombStoreProvider } from './providers/honeycomb-store.provider.js';
import { HoneycombChartDownloadCSVSlot } from './slots/DownloadCSV.js';
import { HoneycombLegend } from './slots/HoneycombLegend.js';
import { Toolbar } from './slots/Toolbar.js';
import { Tooltip } from './slots/Tooltip.js';
import { HoneycombConsumerExportedConfig } from './types/honeycomb-config-properties.js';
import { HoneycombRequiredConfigProps } from './types/honeycomb-internal.js';
import {
  HoneycombChartProps,
  HoneycombChartRef,
  type HoneycombData,
} from './types/honeycomb.js';
import { buildAndDownloadCsv } from './utils/build-and-download-csv.js';
import { isHoneycombChartAccessorDataProps } from './utils/build-honeycomb-config.js';
import { normalizeHoneycombChartData } from './utils/normalize-honeycomb-chart-data.js';
import { normalizeTilesData } from './utils/normalize-tiles-data.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { ChartSeriesActionContext } from '../core/components/series-action/ChartSeriesActionContext.js';
import { getToolbarConfigWithoutIntents } from '../core/components/toolbar/utils/get-toolbar-config-without-intents.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import type { ChartData } from '../core/types/chart-data.js';
import { ChartType } from '../core/types/chart-types.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';

const COMPONENT_NAME = 'HoneycombChart';

const normalizeHoneycombConfig = (
  config: HoneycombRequiredConfigProps,
): HoneycombConsumerExportedConfig => {
  const { toolbar: configuredToolbar, ...remainingConfig } = config;
  const toolbar = configuredToolbar
    ? getToolbarConfigWithoutIntents(configuredToolbar)
    : undefined;

  return { ...remainingConfig, toolbar };
};

/** The honeycomb component */
export const _HoneycombChart = forwardRef<
  HoneycombChartRef,
  PropsWithChildren<HoneycombChartProps>
>((props, forwardedRef) => {
  const {
    data,
    seriesActions,
    width,
    height,
    style: costumerStyle,
    className: customerClassNames,
    loading,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartWidth = coerceSizeValue(width);
  const chartHeight = coerceSizeValue(height) || DEFAULT_HONEYCOMB_HEIGHT;
  const rootRef = useMergeRefs<HoneycombChartRef | HTMLDivElement>([
    forwardedRef,
    containerRef,
  ]);

  const sanitizedData = isHoneycombChartAccessorDataProps(props)
    ? normalizeHoneycombChartData(data as ChartData, props.valueAccessor)
    : normalizeTilesData(data as HoneycombData);

  const config = useHoneycombChartConfig(props);

  const formatterContext = {
    formatter: config.formatter,
    unit: config.unit,
  };

  useImperativeHandle(forwardedRef, () => {
    const consumerConfig = normalizeHoneycombConfig(config);
    return {
      element: containerRef.current,
      downloadData: () => {
        buildAndDownloadCsv(sanitizedData);
      },
      getConfig: () => {
        return JSON.stringify({
          type: ChartType.Honeycomb,
          ...consumerConfig,
        });
      },
    };
  });

  return (
    <div
      {...behavioralTrackingProps}
      ref={rootRef}
      style={{ ...costumerStyle, width: chartWidth, height: chartHeight }}
      className={clsx(defaultChartSize, customerClassNames)}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-dt-component={COMPONENT_NAME}
      aria-label={ariaLabel ?? HONEYCOMB_CHART_ARIA_LABEL}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-details={ariaDetails}
      role="img"
    >
      <HoneycombLoadingContext.Provider value={loading}>
        <HoneycombConfigurationProvider config={config}>
          <HoneycombShapeContext.Provider value={config.shape}>
            <HoneycombColorSchemeContext.Provider value={config.colorScheme}>
              <HoneycombTruncationMode.Provider value={config.truncationMode}>
                <HoneycombFormatterContext.Provider value={formatterContext}>
                  <HoneycombLabelsDisplayContext.Provider
                    value={config.labelsDisplay}
                  >
                    <HoneycombTextSizeContext.Provider value={config.textSize}>
                      <HoneycombFilteredDataProvider
                        data={sanitizedData}
                        config={config}
                      >
                        <HoneycombDatasetTypeProvider>
                          <HoneycombBaseScaleProvider>
                            <ChartSeriesActionContext.Provider
                              value={{
                                // We need a cast here as the provider expects unknown and the
                                // react context will not be able to carry the generic anyways.
                                chartSeriesActions: seriesActions as (
                                  series: unknown,
                                ) => ReactElement<PropsWithChildren>,
                              }}
                            >
                              <HoneycombStoreProvider>
                                <HoneycombMetadataProvider>
                                  <HoneycombChartLayout {...props} />
                                </HoneycombMetadataProvider>
                              </HoneycombStoreProvider>
                            </ChartSeriesActionContext.Provider>
                          </HoneycombBaseScaleProvider>
                        </HoneycombDatasetTypeProvider>
                      </HoneycombFilteredDataProvider>
                    </HoneycombTextSizeContext.Provider>
                  </HoneycombLabelsDisplayContext.Provider>
                </HoneycombFormatterContext.Provider>
              </HoneycombTruncationMode.Provider>
            </HoneycombColorSchemeContext.Provider>
          </HoneycombShapeContext.Provider>
        </HoneycombConfigurationProvider>
      </HoneycombLoadingContext.Provider>
    </div>
  );
});

(
  _HoneycombChart as typeof _HoneycombChart & { displayName: string }
).displayName = DISPLAY_NAME;

/**
 * The `HoneycombChart` displays data in a grid of tiles (shaped as hexagons,
 * squares, or circles), one tile per datapoint.
 *
 * Provide `data` in the unified `ChartData` format — a flat array of objects.
 * The tile value is resolved through the `valueAccessor` prop, which defaults
 * to `"value"` and supports dot-notation for nested fields (e.g.
 * `"metrics.responseTime"`). Values can be numeric (numerical hive) or string
 * (categorical hive). Coloring is controlled with the `colorPalette` prop.
 *
 * @see {@link HoneycombChartAccessorDataProps}
 * @public
 */
export const HoneycombChart = Object.assign(_HoneycombChart, {
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  Legend: HoneycombLegend,
  Intent,
  ColorRule,
  Toolbar,
  DownloadCSV: HoneycombChartDownloadCSVSlot,
  Tooltip,
});
